import { Button, IconButton } from "@rosc/ui/v32/button";
import { Chip } from "@rosc/ui/v32/chip";
import {
  GenerationProgress,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import {
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "@rosc/ui/v32/menu";
import { ChevronDown, Rows3, TextQuote, X } from "lucide-react";
import type { ComponentPropsWithRef, JSX } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// A suggestion chip in the hint, a secondary button in the preview.
const Control = ({
  look,
  ...props
}: { look: "chip" | "button" } & ComponentPropsWithRef<"button">) =>
  look === "chip" ? (
    // Phones: one full-width pill per row (both do not fit side by side),
    // as the buttons in the preview.
    <Chip
      variant="suggestion"
      {...props}
      className={cn("max-sm:w-full max-sm:justify-center", props.className)}
    />
  ) : (
    // Side by side when both fit, else one per row; each as wide as the
    // row allows (the preview sheet's 380px at most).
    <Button
      variant="secondary"
      {...props}
      className={cn("min-w-0 flex-auto max-sm:w-full", props.className)}
    />
  );

// How a pre-chart's attached file is added (clinical lead, 04/11/2026):
// "Spread into sections", or "Summarize and insert" under a heading picked
// from the note's headings (the history heading first marked as the
// default). Chips in the hint above the Ask bar, buttons in the file's
// preview.
export const PreChartFileActions = ({
  look,
  headings,
  defaultHeading,
  disabled = false,
  menuSide = "bottom",
  className,
  onSpread,
  onSummarize,
}: {
  look: "chip" | "button";
  headings: readonly string[];
  defaultHeading: string | null;
  disabled?: boolean;
  menuSide?: "top" | "bottom";
  className?: string;
  onSpread: () => void;
  onSummarize: (heading: string | null) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const summarizeLabel = (
    <>
      <TextQuote aria-hidden />
      {t("preChart.fileAdd.summarize")}
    </>
  );
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      data-testid="pre-chart-file-actions"
    >
      <Control
        look={look}
        data-testid="pre-chart-spread"
        disabled={disabled}
        onClick={onSpread}
      >
        <Rows3 aria-hidden />
        {t("preChart.fileAdd.spread")}
      </Control>
      {headings.length ? (
        <Menu>
          <MenuTrigger asChild disabled={disabled}>
            <Control look={look} data-testid="pre-chart-summarize">
              {summarizeLabel}
              <ChevronDown aria-hidden className="-mr-0.5 text-v3-text-3" />
            </Control>
          </MenuTrigger>
          <MenuContent
            align="start"
            className="max-h-[min(60dvh,360px)] max-w-[calc(100vw-32px)] overflow-y-auto"
            collisionPadding={16}
            data-testid="pre-chart-summarize-menu"
            side={menuSide}
          >
            <MenuLabel>{t("preChart.fileAdd.insertUnder")}</MenuLabel>
            <MenuRadioGroup value={defaultHeading ?? ""}>
              {headings.map((heading) => (
                <MenuRadioItem
                  key={heading}
                  data-testid="pre-chart-summarize-heading"
                  value={heading}
                  onSelect={() => onSummarize(heading)}
                >
                  <span className="min-w-0 flex-1 truncate">{heading}</span>
                  {heading === defaultHeading && (
                    <span className="shrink-0 pl-3 text-[12px] text-v3-text-3">
                      {t("preChart.fileAdd.defaultHeading")}
                    </span>
                  )}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
      ) : (
        // A note without headings: the summary goes in as it is.
        <Control
          look={look}
          data-testid="pre-chart-summarize"
          disabled={disabled}
          onClick={() => onSummarize(null)}
        >
          {summarizeLabel}
        </Control>
      )}
    </div>
  );
};

// The files waiting to be added and the two ways to add them: shown above
// the Ask bar under the note, and above the assistant's own Ask bar when
// the assistant is open (staging check 04/11: with the assistant docked
// open, the bottom dock is hidden and the choice was not offered at all).
export interface PreChartAddOffer {
  count: number;
  // The files by name ("consult.pdf, labs.png", or "4 files").
  label: string;
  headings: readonly string[];
  defaultHeading: string | null;
  onSpread: () => void;
  onSummarize: (heading: string | null) => void;
  onDismiss: () => void;
}

export const PreChartAddHint = ({
  offer,
  className,
}: {
  offer: PreChartAddOffer;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    // The file's name and what to do, then the two ways to add it (they
    // wrap on phones); x in the corner.
    <div
      className={cn(
        "relative rounded-[16px] bg-v3-panel p-3 shadow-v3-hairline-strong",
        className,
      )}
      data-testid="pre-chart-add-hint"
      role="status"
    >
      <p className="min-w-0 pr-10 text-[13px] leading-[18px] lg:pr-8">
        <span className="font-medium text-v3-text">
          {t("preChart.fileAdd.hintTitle", {
            count: offer.count,
            name: offer.label,
          })}
        </span>{" "}
        <span className="text-v3-text-2">
          {t("preChart.fileAdd.hint", { count: offer.count })}
        </span>
      </p>
      <PreChartFileActions
        className="mt-2.5"
        defaultHeading={offer.defaultHeading}
        headings={offer.headings}
        look="chip"
        menuSide="top"
        onSpread={offer.onSpread}
        onSummarize={offer.onSummarize}
      />
      <IconButton
        className="absolute right-1 top-1"
        data-testid="pre-chart-add-hint-dismiss"
        label={t("preChart.fileAdd.hintDismiss")}
        onClick={offer.onDismiss}
      >
        <X />
      </IconButton>
    </div>
  );
};

// While a file is spread or summarized: the shared generation progress
// (#549), "Reading the files…" then "Writing the changes…" (after 3 s, or
// once the edit is being saved); it stays until the change is in the note.
export const PRE_CHART_ADD_STEP_DELAYS = [3000] as const;
export const PreChartAddProgress = ({
  kind,
  label,
  saving,
  plain = false,
  className,
}: {
  kind: "spread" | "summary" | "labs";
  label: string;
  // The edit is written and being saved.
  saving: boolean;
  plain?: boolean;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const timed = useTimedStep(PRE_CHART_ADD_STEP_DELAYS, true, label);
  return (
    <GenerationProgress
      className={className}
      current={saving ? 1 : timed}
      plain={plain}
      steps={[
        {
          key: "reading",
          label: t("generationProgress.steps.readingFiles"),
        },
        {
          key: "writing",
          label: t("generationProgress.steps.writingChanges"),
        },
      ]}
      testId="pre-chart-auto-add"
      title={t(
        kind === "spread"
          ? "preChart.fileAdd.spreading"
          : kind === "labs"
            ? "preChart.labsAdd.adding"
            : "preChart.fileAdd.summarizing",
        { name: label },
      )}
    />
  );
};

// A scan, photo or screenshot being read before it is offered (a lab
// report gets the lab choice): the shared generation progress.
export const PreChartReadingProgress = ({
  label,
  className,
}: {
  label: string;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <GenerationProgress
      className={className}
      current={0}
      steps={[
        {
          key: "reading",
          label: t("generationProgress.steps.readingFiles"),
        },
      ]}
      testId="pre-chart-reading"
      title={t("preChart.labsAdd.reading", { name: label })}
    />
  );
};
